import { call, put, select, take, takeEvery } from "redux-saga/effects";

import {
  SideLoadedBusinessAction,
  SideLoadedDataSetAction,
  uploadSideLoadedDataSetFailure,
  uploadSideLoadedDataSetSuccess,
} from "actions/sideLoadedData";

import { SIDELOADED_BUSINESS_SET } from "constants/actionTypes";

import { closeModal } from "lib/navigation";
import toast from "lib/toast";

import { refreshJWTAndRetry } from "sagas/api";

import { createUploadFileChannel } from "./createUploadFileChannel";

const UPLOAD_URL = "/v2/sideloaded-data-sets/";

/**
 * Pull the readable reason out of a DRF rejection, eg
 * {"source_data": ["CSV file must have a 'Name', 'TradingName', or 'DisplayName' column."]}
 */
function getUploadErrorMessage(response: unknown, statusCode: number): string {
  if (statusCode === 403) {
    return "You do not have permission to upload data sets.";
  }
  const fieldErrors = (response as Record<string, string[] | string>) || {};
  const firstError = Object.values(fieldErrors).flat()[0];
  return typeof firstError === "string"
    ? firstError
    : "The file could not be uploaded. Please try again.";
}

function* uploadDataSet(action) {
  const {
    name,
    meta: { file },
  } = action;

  const { auth } = yield select();
  const channel = yield call(
    createUploadFileChannel,
    UPLOAD_URL,
    file,
    { name },
    auth,
    "POST",
    "source_data",
  );

  while (true) {
    const {
      err,
      status_code: statusCode,
      response,
      success,
    } = yield take(channel);

    if (err) {
      if (statusCode === 401) {
        yield call(refreshJWTAndRetry, action);
        return;
      }
      const errorMessage = getUploadErrorMessage(response, statusCode);
      yield put(uploadSideLoadedDataSetFailure(errorMessage));
      // The modal stays open, showing this same message above the dropzone -
      // the file has to be corrected and dropped again.
      toast.error(`${name}: ${errorMessage}`);
      return;
    }

    if (success) {
      yield put(uploadSideLoadedDataSetSuccess());
      // The CSV is parsed within the request, so both the data set and its
      // records exist by now. Pusher excludes the socket that uploaded, so this
      // is the only thing that brings them in for the uploader.
      yield* refreshDataSets();
      yield* refreshRecords();
      toast.success(
        `${response.name}: ${response.record_count.toLocaleString()} records loaded.`,
      );
      closeModal(); // Close the Legacy Data Set upload modal
      return;
    }
  }
}

function* refreshRecords() {
  const state = yield select();
  yield put(
    SideLoadedBusinessAction.requestChanges({
      changesSince: state.sideLoadedBusinesses.lastModifiedTimestamp,
    }),
  );
}

function* refreshDataSets() {
  const state = yield select();
  yield put(
    SideLoadedDataSetAction.requestChanges({
      changesSince: state.sideLoadedDataSets.lastModifiedTimestamp,
    }),
  );
}

function* refreshFromSocket() {
  yield* refreshRecords();
  yield* refreshDataSets();
}

export default function* sideLoadedDataSetsSaga() {
  yield takeEvery(SIDELOADED_BUSINESS_SET.UPLOAD.REQUEST, uploadDataSet);
  yield takeEvery(
    SIDELOADED_BUSINESS_SET.REFRESH.FROM_SOCKET,
    refreshFromSocket,
  );
  // Pusher excludes the socket that made the request, so the deleting client
  // never hears about its own delete - and nothing local drops the deleted data
  // set's records, which live in their own slice.
  yield takeEvery(SIDELOADED_BUSINESS_SET.DELETE.SUCCESS, refreshRecords);
}
